export type ApiErrorInfo = {
  message: string;
  status?: number;
  code?: string;
};

const getEmployeeNotFoundMessage = (message?: string): string | null => {
  const match = (message ?? "").match(/profile details for (.+) not found/i);
  return match?.[1]
    ? `Employee ${match[1]} not found`
    : null;
};

// Normalize various error shapes (Axios, Fetch) into a friendly message
export const extractApiErrorMessage = (
  error: any,
  defaultMessage = "Unexpected error occurred",
): string => {
  const status: number | undefined = error?.response?.status;
  const statusText: string | undefined = error?.response?.statusText;
  const code: string | undefined = error?.code;

  // Extract message from various response formats
  let dataMessage: string | undefined;
  if (typeof error?.response?.data === "string") {
    dataMessage = error?.response?.data;
  } else if (error?.response?.data?.message) {
    dataMessage = error?.response?.data?.message;
  } else if (error?.response?.data?.error) {
    dataMessage = error?.response?.data?.error;
  } else if (error?.response?.data?.detail) {
    dataMessage = error?.response?.data?.detail;
  }

  const employeeNotFoundMessage = getEmployeeNotFoundMessage(dataMessage);
  if (employeeNotFoundMessage) {
    return employeeNotFoundMessage;
  }

  let message: string;

  // Friendly overrides by status
  switch (status) {
    case 400:
      message =
        dataMessage ||
        "Your request could not be completed. We apologize for the inconvenience. Please try again.";
      break;

    case 403:
      message =
        dataMessage ||
        "You do not have permission to perform this action. We apologize for the inconvenience.";
      break;

    case 404:
      message =
        dataMessage ||
        "The requested page or resource is not available. We apologize for the inconvenience.";
      break;

    case 405:
      message =
        dataMessage ||
        "This action is not supported at this time. We apologize for the inconvenience.";
      break;

    case 500:
      // For 500 errors, prioritize backend message
      message =
        dataMessage ||
        error?.response?.data?.message ||
        error?.message ||
        "Reimbursement Portal is temporarily unavailable. We apologize for the inconvenience.";
      break;

    case 504:
      message =
        dataMessage ||
        "The request is taking longer than expected. We apologize for the inconvenience. Please try again.";
      break;

    default:
      message =
        dataMessage ||
        error?.message ||
        statusText ||
        defaultMessage;
  }

  // Network error
  if (!error?.response && code === "ERR_NETWORK") {
    message = "Network error. Please check your connection and try again.";
  }
  return message;
};

/**
 * Simple error message extraction that matches the exact duplicated pattern
 * Used for maintaining exact functionality in Redux slices
 */
export const getSimpleErrorMessage = (error: any, defaultMessage: string): string => {
  return extractApiErrorMessage(error, defaultMessage);
};

export const extractApiErrorInfo = (
  error: any,
  defaultMessage = "Unexpected error occurred",
): ApiErrorInfo => ({
  message: extractApiErrorMessage(error, defaultMessage),
  status: error?.response?.status,
  code: error?.code,
});
